import React, { useState } from "react";
import { DatePicker, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";

const { RangePicker } = DatePicker;

export enum DatePreset {
  Today = "today",
  Yesterday = "yesterday",
  Last7 = "last7",
  Last30 = "last30",
  ThisWeek = "thisWeek",
  LastWeek = "lastWeek",
  ThisMonth = "thisMonth",
  LastMonth = "lastMonth",
  Last90 = "last90",
  ThisYear = "thisYear",
  Custom = "custom",
}

export default function DateRangeSelector() {
  const { t } = useTranslation();

  const PRESETS = Object.values(DatePreset).map((preset) => ({
    value: preset,
    label: t(`pages.dashboard.datePresets.${preset}`),
  }));

  const calculateRange = (preset: DatePreset): [Dayjs, Dayjs] | null => {
    switch (preset) {
      case DatePreset.Today:
        return [dayjs(), dayjs()];
      case DatePreset.Yesterday:
        return [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")];
      case DatePreset.Last7:
        return [dayjs().subtract(7, "day"), dayjs()];
      case DatePreset.Last30:
        return [dayjs().subtract(30, "day"), dayjs()];
      case DatePreset.ThisWeek:
        return [dayjs().startOf("week"), dayjs().endOf("week")];
      case DatePreset.LastWeek:
        return [
          dayjs().subtract(1, "week").startOf("week"),
          dayjs().subtract(1, "week").endOf("week"),
        ];
      case DatePreset.ThisMonth:
        return [dayjs().startOf("month"), dayjs().endOf("month")];
      case DatePreset.LastMonth:
        return [
          dayjs().subtract(1, "month").startOf("month"),
          dayjs().subtract(1, "month").endOf("month"),
        ];
      case DatePreset.Last90:
        return [dayjs().subtract(90, "day"), dayjs()];
      case DatePreset.ThisYear:
        return [dayjs().startOf("year"), dayjs().endOf("year")];
      case DatePreset.Custom:
      default:
        return null;
    }
  };

  const [preset, setPreset] = useState<DatePreset>(DatePreset.ThisMonth);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(
    calculateRange(DatePreset.ThisMonth),
  );

  const handlePresetChange = (value: DatePreset) => {
    setPreset(value);
    setRange(calculateRange(value));
  };
  const handleRangeChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (values && values[0] && values[1]) {
      setRange([values[0], values[1]]);
      setPreset(DatePreset.Custom);
    }
  };

  return (
    <div className={styles.filter}>
      <Select
        className={styles.preset}
        value={preset}
        onChange={handlePresetChange}
        options={PRESETS}
      />
      <RangePicker
        value={range}
        onChange={handleRangeChange}
        allowClear={false}
      />
    </div>
  );
}
